import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import type { AutoBatchResponse, AutoCandidate } from "@opus-dt/shared-types";
import type { ObjectiveAxis } from "../objective/objective-meta";
import { fmtMetric, fmtPp, objDelta, objMetric, oeeDeltaValue } from "./metrics";

interface CandidateRankingTableProps {
  batch: AutoBatchResponse;
  baseline: AutoCandidate | null;
  ranked: AutoCandidate[];
  failed: AutoCandidate[];
  axis: ObjectiveAxis;
  showOee: boolean;
}

// A mesma informação do gráfico, em números. Não é um resto da tela antiga: é a
// alternativa textual que o gráfico deve ter (quem precisa do valor exato, quem lê por leitor de
// tela e quem vai copiar para um relatório). Fica recolhida porque não é o modo de leitura padrão.
export function CandidateRankingTable({
  batch,
  baseline,
  ranked,
  failed,
  axis,
  showOee,
}: CandidateRankingTableProps) {
  const head = "font-medium normal-case tracking-normal";
  const num = "text-right font-mono tabular-nums";

  return (
    // `table-fixed`: sem ele o nome do candidato (a única coluna que pode crescer) era espremido
    // até truncar em "C-2 — ciclo −10% nas ..." com meia tabela vazia à direita.
    <Table className="table-fixed">
      <TableHead>
        <tr>
          <TableHeaderCell className={`${head} w-[42%]`}>Candidato</TableHeaderCell>
          <TableHeaderCell className={`${head} text-right`}>{axis.short}</TableHeaderCell>
          <TableHeaderCell className={`${head} text-right`}>
            Δ {axis.short.toLowerCase()}
          </TableHeaderCell>
          {showOee ? (
            <>
              <TableHeaderCell className={`${head} text-right`}>OEE</TableHeaderCell>
              <TableHeaderCell className={`${head} text-right`}>Δ OEE</TableHeaderCell>
            </>
          ) : null}
        </tr>
      </TableHead>
      <TableBody>
        {baseline ? (
          <TableRow>
            <TableCell className="text-text-secondary">Base</TableCell>
            <TableCell className={num}>{fmtMetric(objMetric(baseline, batch))}</TableCell>
            <TableCell className={`${num} text-text-tertiary`}>—</TableCell>
            {showOee ? (
              <>
                <TableCell className={num}>{fmtOee(baseline.oee)}</TableCell>
                <TableCell className={`${num} text-text-tertiary`}>—</TableCell>
              </>
            ) : null}
          </TableRow>
        ) : null}

        {ranked.map((c) => {
          const delta = objDelta(c, baseline, batch);
          return (
            <TableRow key={c.simulationId}>
              <TableCell className="truncate">{c.label ?? "Candidato"}</TableCell>
              <TableCell className={num}>{fmtMetric(objMetric(c, batch))}</TableCell>
              <TableCell className={num}>{delta?.text ?? "—"}</TableCell>
              {showOee ? (
                <>
                  <TableCell className={num}>{fmtOee(c.oee)}</TableCell>
                  <TableCell className={num}>{fmtPp(oeeDeltaValue(c, baseline))}</TableCell>
                </>
              ) : null}
            </TableRow>
          );
        })}

        {failed.map((c) => (
          <TableRow key={c.simulationId}>
            <TableCell className="truncate text-text-secondary">{c.label ?? "Candidato"}</TableCell>
            <TableCell className="text-right text-text-tertiary" colSpan={showOee ? 4 : 2}>
              falhou
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function fmtOee(oee: number | null): string {
  return oee == null ? "—" : `${oee.toFixed(1)}%`;
}
